import React, { useState } from 'react';

import { apiUrl } from '../utils/apiBase';
import type { MatchItem, MatchRules, SetEntryOptions, SetScore } from './scoring-types';

export type SetValue = { p1: number | ''; p2: number | '' };
export type Winner = 1 | 2 | null;

function isCanonicalSet(setEntry: SetEntryOptions, set: SetValue): boolean {
  if (set.p1 === '' || set.p2 === '') {
    return false;
  }

  return setEntry.winnerByLoser[String(Math.min(set.p1, set.p2))] === Math.max(set.p1, set.p2);
}

export function setViolation(setEntry: SetEntryOptions, set: SetValue): string | null {
  if (set.p1 === '' || set.p2 === '' || isCanonicalSet(setEntry, set)) {
    return null;
  }

  if (set.p1 === set.p2) {
    return 'Remis w secie nie jest możliwy.';
  }

  const loser = Math.min(set.p1, set.p2);
  const expected = setEntry.winnerByLoser[String(loser)];

  if (expected === undefined) {
    return `Wynik ${set.p1}:${set.p2} nie jest możliwy w tym formacie.`;
  }

  return `Przy ${loser} zwycięzca ma ${expected}, nie ${Math.max(set.p1, set.p2)}.`;
}

function withCompletingScore(
  match: MatchItem,
  set: SetValue,
  side: 'p1' | 'p2',
  value: number | '',
): SetValue {
  const next: SetValue = side === 'p1' ? { ...set, p1: value } : { ...set, p2: value };

  if (value === '' || value >= match.rules.pointsPerSet) {
    return next;
  }

  const winner = match.setEntry.winnerByLoser[String(value)];

  if (winner === undefined || isCanonicalSet(match.setEntry, next)) {
    return next;
  }

  return side === 'p1' ? { ...next, p2: winner } : { ...next, p1: winner };
}

export function plannedSetCount(rules: MatchRules): number {
  return rules.setsPlayed ?? rules.bestOf;
}

// Mecz przerwany między setami: bez wyniku, ale z zapisanymi skończonymi setami.
export function partialSetsOf(match: MatchItem): SetScore[] {
  if (match.winnerSide !== null || match.sets.length > 0) {
    return [];
  }

  return match.partialSets ?? [];
}

export function isInProgress(match: MatchItem): boolean {
  return partialSetsOf(match).length > 0;
}

export function initialSets(match: MatchItem): SetValue[] {
  const source = match.sets.length > 0 ? match.sets : partialSetsOf(match);

  return Array.from({ length: plannedSetCount(match.rules) }, (_, index) => {
    const played = source[index];

    if (!played) {
      return { p1: '', p2: '' };
    }

    return { p1: played.p1, p2: played.p2 };
  });
}

function setsToWin(bestOf: number): number {
  return Math.ceil(bestOf / 2);
}

export function computeWinner(sets: SetValue[], rules: MatchRules): Winner {
  const decided = sets.filter((set) => set.p1 !== '' && set.p2 !== '' && set.p1 !== set.p2);
  const side1 = decided.filter((set) => set.p1 > set.p2).length;
  const side2 = decided.length - side1;

  if (rules.setsPlayed != null) {
    if (decided.length < rules.setsPlayed || side1 === side2) {
      return null;
    }

    return side1 > side2 ? 1 : 2;
  }

  const needed = setsToWin(rules.bestOf);

  if (side1 >= needed && side1 > side2) {
    return 1;
  }

  if (side2 >= needed && side2 > side1) {
    return 2;
  }

  return null;
}

export function isDraw(sets: SetValue[], rules: MatchRules): boolean {
  if (rules.setsPlayed == null) {
    return false;
  }

  const decided = sets.filter((set) => set.p1 !== '' && set.p2 !== '' && set.p1 !== set.p2);
  const side1 = decided.filter((set) => set.p1 > set.p2).length;

  return decided.length === rules.setsPlayed && side1 * 2 === decided.length;
}

export function visibleSetCount(sets: SetValue[], rules: MatchRules): number {
  if (rules.setsPlayed != null) {
    return rules.setsPlayed;
  }

  const needed = setsToWin(rules.bestOf);
  let side1 = 0;
  let side2 = 0;

  for (let index = 0; index < rules.bestOf; index += 1) {
    const set = sets[index];

    if (!set || set.p1 === '' || set.p2 === '') {
      return Math.max(needed, index + 1);
    }

    if (set.p1 > set.p2) {
      side1 += 1;
    } else if (set.p2 > set.p1) {
      side2 += 1;
    }

    if (side1 >= needed || side2 >= needed) {
      return Math.max(needed, index + 1);
    }
  }

  return rules.bestOf;
}

// Remis przyjeżdża bez zwycięzcy, a od meczu nierozegranego odróżniają go
// wpisane sety — tak samo zapisuje go ingest plików i tak samo wysyła ten hook.
export function isDrawn(match: MatchItem): boolean {
  return match.winnerSide === null && match.sets.length > 0;
}

// Walkower to zwycięzca bez setów — nikt nie zagrał, więc rating go pomija.
export function savedWalkover(match: MatchItem): Winner {
  return match.sets.length === 0 ? currentWinner(match) : null;
}

export function currentWinner(match: MatchItem): Winner {
  if (match.winnerSide === 'SIDE1') {
    return 1;
  }

  if (match.winnerSide === 'SIDE2') {
    return 2;
  }

  return null;
}

function filledSets(sets: SetValue[]): SetScore[] {
  return sets
    .filter((set) => set.p1 !== '' && set.p2 !== '')
    .map((set) => ({ p1: Number(set.p1), p2: Number(set.p2) }));
}

export interface MatchScoreDraft {
  sets: SetValue[];
  visibleSets: SetValue[];
  winner: Winner;
  // Strona, która wygrała walkowerem; null = mecz rozegrany na sety.
  walkover: Winner;
  // Sety już wpisane dają remis — do zapisu nie trzeba zwycięzcy.
  drawn: boolean;
  blocked: boolean;
  // Wpisane sety nie rozstrzygają meczu — można je zapisać i dokończyć później.
  canSavePartial: boolean;
  submitting: boolean;
  error: string | null;
  updateSet: (index: number, side: 'p1' | 'p2', raw: string) => void;
  setWalkover: (winner: Winner) => void;
  reset: () => void;
  submit: (event: React.FormEvent) => Promise<void>;
  savePartial: () => Promise<void>;
}

export function useMatchScoreDraft(
  match: MatchItem,
  token: string,
  onSaved: () => void,
): MatchScoreDraft {
  const [sets, setSets] = useState<SetValue[]>(() => initialSets(match));
  const [walkover, setWalkover] = useState<Winner>(() => savedWalkover(match));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const winner = walkover ?? computeWinner(sets, match.rules);
  const drawn = walkover === null && winner === null && isDraw(sets, match.rules);
  const visibleSets = walkover === null ? sets.slice(0, visibleSetCount(sets, match.rules)) : [];
  const blocked = visibleSets.some((set) => setViolation(match.setEntry, set) !== null);
  const filled = filledSets(sets);
  const halfFilled = sets.some((set) => (set.p1 === '') !== (set.p2 === ''));
  const alreadyScored = match.winnerSide !== null || match.sets.length > 0;
  const canSavePartial =
    !alreadyScored &&
    walkover === null &&
    !blocked &&
    !halfFilled &&
    winner === null &&
    !drawn &&
    (filled.length > 0 || isInProgress(match));

  function updateSet(index: number, side: 'p1' | 'p2', raw: string): void {
    const parsed = raw === '' ? '' : Number(raw);
    const value = parsed === '' || Number.isNaN(parsed) ? '' : parsed;

    setSets((previous) =>
      previous.map((set, i) => (i === index ? withCompletingScore(match, set, side, value) : set)),
    );
  }

  function reset(): void {
    setSets(initialSets(match));
    setWalkover(savedWalkover(match));
    setError(null);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();

    if (walkover !== null) {
      await send('POST', 'result', { sets: [], winnerSide: walkover });

      return;
    }

    const drawn = winner === null && isDraw(sets, match.rules);

    if (winner === null && !drawn) {
      setError('Uzupełnij sety — nie da się ustalić zwycięzcy.');

      return;
    }

    // 0 = remis: API i event log znają ten sam kształt co pliki z ingestu.
    const winnerSide = drawn ? 0 : winner;

    await send('POST', 'result', { sets: filled, winnerSide });
  }

  async function savePartial(): Promise<void> {
    await send('PUT', 'partial-result', { sets: filled });
  }

  async function send(method: 'POST' | 'PUT', path: string, payload: unknown): Promise<void> {
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        apiUrl(`/api/public/scoring/${token}/matches/${match.id}/${path}`),
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (response.status === 429) {
        setError('Zbyt wiele żądań — poczekaj chwilę i spróbuj ponownie.');

        return;
      }

      if (response.status === 403) {
        setError('Kolejka jest już zamknięta — wyniki niedostępne do edycji.');

        return;
      }

      if (!response.ok) {
        const body = await response.json() as { error?: string };
        setError(body.error ?? `Błąd (${response.status})`);

        return;
      }

      onSaved();
    } catch {
      setError('Błąd sieci — sprawdź połączenie.');
    } finally {
      setSubmitting(false);
    }
  }

  return {
    sets,
    visibleSets,
    winner,
    walkover,
    drawn,
    blocked,
    canSavePartial,
    submitting,
    error,
    updateSet,
    setWalkover,
    reset,
    submit,
    savePartial,
  };
}
